import DOMPurify from "dompurify";

/**
 * GIL-004 helpers: escape untrusted strings before injecting into HTML sinks
 * (DataTables render, jQuery .html(), SweetAlert html, document.write).
 */

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Attribute-safe escaping (same entity set as escapeHtml). */
export function escapeHtmlAttr(value) {
  return escapeHtml(value);
}

/**
 * Allow only safe CSS color values for inline style="color:...".
 * Rejects anything that could break out of a style attribute.
 */
export function safeCssColor(color, fallback = "#000000") {
  const s = String(color ?? "").trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s;
  if (/^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(s)) {
    return s;
  }
  return fallback;
}

/** Sanitize intentional HTML (print views, Quill email bodies). */
export function sanitizeHtml(dirty, config) {
  return DOMPurify.sanitize(String(dirty ?? ""), {
    USE_PROFILES: { html: true },
    ...config,
  });
}

/** Quill / email body: allow common formatting, strip scripts/handlers. */
export function sanitizeRichHtml(dirty) {
  return DOMPurify.sanitize(String(dirty ?? ""), {
    ALLOWED_TAGS: [
      "a", "b", "br", "div", "em", "h1", "h2", "h3", "h4", "hr", "i", "li",
      "ol", "p", "span", "strong", "table", "tbody", "td", "th", "thead", "tr", "u", "ul",
      "img",
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel", "src", "alt", "title", "width", "height",
      "style", "class", "colspan", "rowspan",
    ],
    ALLOW_DATA_ATTR: false,
  });
}

/** DataTables plain-text cell (forces HTML-escaped display). */
export function dtText(value, empty = "") {
  const s = value == null || value === "" ? empty : value;
  return escapeHtml(s);
}

/**
 * Status badge HTML with whitelisted color mapping.
 * @param {string} status
 * @param {(normalized: string, raw: string) => string} [resolveColor]
 */
export function renderStatusHtml(status, resolveColor) {
  const raw = String(status ?? "");
  const normalized = raw.trim().toLowerCase();
  let color = "#000000";
  const override =
    typeof resolveColor === "function" ? resolveColor(normalized, raw) : undefined;
  if (override) {
    color = override;
  } else if (normalized === "open") {
    color = "#1976d2";
  } else if (normalized === "rejected") {
    color = "#ff0000";
  } else if (normalized === "wip") {
    color = "#1976d2";
  } else if (normalized === "pending" || normalized.includes("pending")) {
    color = "#ff9800";
  } else if (
    normalized === "approved" ||
    normalized === "reopen" ||
    normalized === "closed"
  ) {
    color = "#4caf50";
  }
  return `<span style="color: ${safeCssColor(color)}; font-weight: 600;">${escapeHtml(raw || "-")}</span>`;
}

/**
 * SR link used by list DataTables (click handled via class + data-* attrs).
 */
export function renderSrLinkHtml({
  parentSrCode = "",
  childSrCode = "",
  label = "",
  className = "sr-link",
  idAttr,
} = {}) {
  const parent = String(parentSrCode ?? "");
  const child = String(childSrCode ?? "");
  const text = String(label || child || parent || "");
  const idPart =
    idAttr != null && idAttr !== ""
      ? ` data-id="${escapeHtmlAttr(idAttr)}"`
      : "";
  return (
    `<a href="#" class="${escapeHtmlAttr(className)}" ` +
    `data-parent-sr-code="${escapeHtmlAttr(parent)}" ` +
    `data-child-sr-code="${escapeHtmlAttr(child)}"${idPart}>` +
    `${escapeHtml(text)}</a>`
  );
}
